import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logoutUser, extendSession, checkAuthValidity } from "../redux/action/actions";
import { useNavigate } from "react-router-dom";
import {
  IconLogoColor,
  IconLogoText,
  IconChevronDown,
  EnglishFlag,
  IconVoice,
  PenTyping,
  VocabDaily,
  GrammarBook,
  HatTestGraduate,
  JapaneseFlag,
  ResourceIcon,
  AnalysisIcon,
  AnnounceIcon,
  ChibaLogoNoColor,
  Volumeware,
  RepeatAudioIcon,
} from "../assets/svg/svgHeader";

import { ImageChibaHeader } from "../assets/images/image";

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const [isAnnounceOpen, setIsAnnounceOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  
  // Redux
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector(state => state.auth);
  const [timeLeft, setTimeLeft] = useState(null);
  
  // Mobile menu states
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileLanguageOpen, setIsMobileLanguageOpen] = useState(false);
  const [isMobileResourcesOpen, setIsMobileResourcesOpen] = useState(false);
  const [isMobileAnalysisOpen, setIsMobileAnalysisOpen] = useState(false);
  const [isMobileAnnounceOpen, setIsMobileAnnounceOpen] = useState(false);
  const [isMobileProfileOpen, setIsMobileProfileOpen] = useState(false);

  const currentPath = useLocation().pathname;

  // Calculate time left until session expires
  useEffect(() => {
    if (user && user.expirationTime) {
      const updateTimeLeft = () => {
        const now = new Date();
        const expiration = new Date(user.expirationTime);
        const diff = expiration.getTime() - now.getTime();
        
        if (diff <= 0) {
          setTimeLeft(null);
          dispatch(checkAuthValidity()); // This will logout if expired
        } else {
          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          
          setTimeLeft({ days, hours, minutes, total: diff });
        }
      };

      updateTimeLeft();
      const interval = setInterval(updateTimeLeft, 60000); // Update every minute

      return () => clearInterval(interval);
    } else {
      setTimeLeft(null);
    }
  }, [user, dispatch]);

  // Handle logout
  const handleLogout = () => {
    dispatch(logoutUser());
    closeAllDropdowns();
    closeAllMobileMenus();
  };

  // Handle session extension
  const handleExtendSession = () => {
    dispatch(extendSession());
    closeAllDropdowns();
    closeAllMobileMenus();
  };

  // Handle login navigation
  const handleLoginClick = () => {
    navigate('/login');
    closeAllDropdowns();
    closeAllMobileMenus();
  };

  // Helper function to close all mobile menus
  const closeAllMobileMenus = () => {
    setIsMobileMenuOpen(false);
    setIsMobileLanguageOpen(false);
    setIsMobileResourcesOpen(false);
    setIsMobileAnalysisOpen(false);
    setIsMobileAnnounceOpen(false);
    setIsMobileProfileOpen(false);
  };

  // Helper function to close all desktop dropdowns
  const closeAllDropdowns = () => {
    setIsLanguageOpen(false);
    setIsResourcesOpen(false);
    setIsAnalysisOpen(false);
    setIsAnnounceOpen(false);
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className="select-none z-50 fixed top-0 left-0 right-0 bg-black/95 backdrop-blur-md border-b border-cyan-400/30 shadow-neon-cyan">
        {/* Cyberpunk grid background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(cyan 1px, transparent 1px),
              linear-gradient(90deg, cyan 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        {/* Animated top border */}
        
        <div className="max-w-7xl mx-auto relative">
          <div className="flex items-center justify-between h-[60px] px-4 lg:px-6">
            
            {/* Logo Section */}
            <div className="flex items-center gap-8">
              <NavLink
                to="/"
                className="flex items-center gap-3 group transition-all duration-300 hover:scale-105"
                onClick={closeAllDropdowns}
              >
                <div className="relative">
                  <img 
                    loading="lazy" 
                    className="w-auto h-12 transition-all duration-300 group-hover:rotate-3 filter brightness-110 contrast-110" 
                    src={ImageChibaHeader} 
                    alt="Logo" 
                  />
                  <div className="absolute inset-0 bg-cyan-400/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="relative">
                  {/* <IconLogoText className="w-auto h-12 transition-all duration-300 group-hover:opacity-80 filter brightness-110" /> */}
                  {/* <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 to-pink-400/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div> */}
                </div>
              </NavLink>
              
              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center gap-2">
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl font-mono font-bold text-sm transition-all duration-300 border ${
                      isActive
                        ? "text-cyan-400 bg-cyan-400/10 border-cyan-400/50 shadow-neon-cyan"
                        : "text-gray-300 border-transparent hover:text-cyan-400 hover:bg-cyan-400/5 hover:border-cyan-400/30"
                    }`
                  }
                  onClick={closeAllDropdowns}
                >
                  HOME
                </NavLink>
                
                <NavLink
                  to="/resources"
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl font-mono font-bold text-sm transition-all duration-300 border ${
                      isActive
                        ? "text-green-400 bg-green-400/10 border-green-400/50 shadow-neon-green"
                        : "text-gray-300 border-transparent hover:text-green-400 hover:bg-green-400/5 hover:border-green-400/30"
                    }`
                  }
                  onClick={closeAllDropdowns}
                >
                  COURSES
                </NavLink>
                
                {/* Languages Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => {
                      setIsLanguageOpen(!isLanguageOpen);
                      setIsResourcesOpen(false);
                      setIsAnalysisOpen(false);
                      setIsAnnounceOpen(false);
                      setIsMenuOpen(false);
                    }}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl font-mono font-bold text-sm transition-all duration-300 border ${
                      isLanguageOpen
                        ? "text-pink-400 bg-pink-400/10 border-pink-400/50 shadow-neon-pink"
                        : "text-gray-300 border-transparent hover:text-pink-400 hover:bg-pink-400/5 hover:border-pink-400/30"
                    }`}
                  >
                    LANGUAGES
                    <IconChevronDown 
                      className={`w-4 h-4 transition-transform duration-300 ${
                        isLanguageOpen ? "rotate-180" : ""
                      }`} 
                    />
                  </button>
                  
                  {isLanguageOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsLanguageOpen(false)}
                      />
                      <div className="absolute top-full left-0 mt-3 w-[700px] bg-black/90 backdrop-blur-md rounded-2xl border border-pink-400/30 shadow-neon-pink overflow-hidden z-20">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-pink-400 to-transparent"></div>
                        <div className="grid grid-cols-2 divide-x divide-gray-700">
                          {/* English Section */}
                          <div className="p-6">
                            <div className="flex items-center gap-3 mb-6 p-3 bg-cyan-400/10 border border-cyan-400/30 rounded-xl">
                              <EnglishFlag className="w-7 h-7" />
                              <span className="font-bold text-cyan-400 font-mono tracking-wider">ENGLISH</span>
                            </div>
                            <div className="space-y-2">
                              <NavLink 
                                to="/repeat-audio"
                                className="flex items-center gap-4 p-4 rounded-xl border border-transparent hover:border-cyan-400/30 hover:bg-cyan-400/5 transition-all duration-300 group"
                                onClick={() => setIsLanguageOpen(false)}
                              >
                                <div className="w-10 h-10 bg-black border border-cyan-400/50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                  <Volumeware className="w-5 h-5 fill-cyan-400" />
                                </div>
                                <span className="text-sm text-gray-300 font-mono group-hover:text-cyan-400 transition-colors duration-300">{'>'} Repeat Audio</span>
                              </NavLink>
                              <NavLink 
                                to="/vocabulary-daily"
                                className="flex items-center gap-4 p-4 rounded-xl border border-transparent hover:border-cyan-400/30 hover:bg-cyan-400/5 transition-all duration-300 group"
                                onClick={() => setIsLanguageOpen(false)}
                              >
                                <div className="w-10 h-10 bg-black border border-cyan-400/50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                  <VocabDaily className="w-5 h-5 fill-cyan-400" />
                                </div>
                                <span className="text-sm text-gray-300 font-mono group-hover:text-cyan-400 transition-colors duration-300">{'>'} Vocabulary Daily</span>
                              </NavLink>
                            </div>
                          </div>
                          
                          {/* Japanese Section */}
                          <div className="p-6">
                            <div className="flex items-center gap-3 mb-6 p-3 bg-pink-400/10 border border-pink-400/30 rounded-xl">
                              <JapaneseFlag className="w-7 h-7" />
                              <span className="font-bold text-pink-400 font-mono tracking-wider">JAPANESE</span>
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center gap-4 p-4 rounded-xl border border-transparent hover:border-pink-400/30 hover:bg-pink-400/5 transition-all duration-300 cursor-pointer group">
                                <div className="w-10 h-10 bg-black border border-pink-400/50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                  <IconVoice className="w-5 h-5 fill-pink-400" />
                                </div>
                                <span className="text-sm text-gray-300 font-mono group-hover:text-pink-400 transition-colors duration-300">{'>'} Kaiwa (AI)</span>
                              </div>
                              <NavLink 
                                to="/vocabulary-japanese"
                                className="flex items-center gap-4 p-4 rounded-xl border border-transparent hover:border-pink-400/30 hover:bg-pink-400/5 transition-all duration-300 group"
                                onClick={() => setIsLanguageOpen(false)}
                              >
                                <div className="w-10 h-10 bg-black border border-pink-400/50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                  <VocabDaily className="w-5 h-5 fill-pink-400" />
                                </div>
                                <span className="text-sm text-gray-300 font-mono group-hover:text-pink-400 transition-colors duration-300">{'>'} Japanese Vocabulary</span>
                              </NavLink>
                              <div className="flex items-center gap-4 p-4 rounded-xl border border-transparent hover:border-pink-400/30 hover:bg-pink-400/5 transition-all duration-300 cursor-pointer group">
                                <div className="w-10 h-10 bg-black border border-pink-400/50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                  <HatTestGraduate className="w-5 h-5 fill-pink-400" />
                                </div>
                                <span className="text-sm text-gray-300 font-mono group-hover:text-pink-400 transition-colors duration-300">{'>'} JLPT N5-N2</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                <NavLink
                  to="/blogs"
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl font-mono font-bold text-sm transition-all duration-300 border ${
                      isActive
                        ? "text-purple-400 bg-purple-400/10 border-purple-400/50 shadow-neon-purple"
                        : "text-gray-300 border-transparent hover:text-purple-400 hover:bg-purple-400/5 hover:border-purple-400/30"
                    }`
                  }
                  onClick={closeAllDropdowns}
                >
                  BLOGS
                </NavLink>
              </nav>
            </div>

            {/* Right Section - Desktop */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Resources */}
              <div className="relative">
                <button
                  onClick={() => {
                    setIsResourcesOpen(!isResourcesOpen);
                    setIsLanguageOpen(false);
                    setIsAnalysisOpen(false);
                    setIsAnnounceOpen(false);
                    setIsMenuOpen(false);
                  }}
                  className="p-3 rounded-xl text-gray-400 hover:text-cyan-400 hover:bg-cyan-400/10 border border-transparent hover:border-cyan-400/30 transition-all duration-300 group"
                >
                  <ResourceIcon className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                </button>
                
                {isResourcesOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsResourcesOpen(false)}
                    />
                    <div className="absolute top-full right-0 mt-3 w-80 bg-black/90 backdrop-blur-md rounded-2xl border border-cyan-400/30 shadow-neon-cyan overflow-hidden z-20">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent"></div>
                      <div className="p-4">
                        <div className="text-center mb-4">
                          <span className="text-cyan-400 font-mono text-sm tracking-wider">RESOURCE DATABASE</span>
                        </div>
                        <div className="space-y-2">
                          <div className="p-4 rounded-xl border border-cyan-400/20 hover:border-cyan-400/40 hover:bg-cyan-400/5 transition-all duration-300 cursor-pointer group">
                            <div className="font-bold text-cyan-400 mb-1 font-mono group-hover:scale-105 transition-transform duration-300">AI NEURAL CORE</div>
                            <div className="text-xs text-gray-400 font-mono">{'>'} AI Learning Resources</div>
                          </div>
                          <div className="p-4 rounded-xl border border-green-400/20 hover:border-green-400/40 hover:bg-green-400/5 transition-all duration-300 cursor-pointer group">
                            <div className="font-bold text-green-400 mb-1 font-mono group-hover:scale-105 transition-transform duration-300">MLOPS SYSTEM</div>
                            <div className="text-xs text-gray-400 font-mono">{'>'} Operations & Deployment</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Analytics */}
              <div className="relative">
                <button
                  onClick={() => {
                    setIsAnalysisOpen(!isAnalysisOpen);
                    setIsLanguageOpen(false);
                    setIsResourcesOpen(false);
                    setIsAnnounceOpen(false);
                    setIsMenuOpen(false);
                  }}
                  className="p-3 rounded-xl text-gray-400 hover:text-green-400 hover:bg-green-400/10 border border-transparent hover:border-green-400/30 transition-all duration-300 group"
                >
                  <AnalysisIcon className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                </button>
                
                {isAnalysisOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsAnalysisOpen(false)}
                    />
                    <div className="absolute top-full right-0 mt-3 w-80 bg-black/90 backdrop-blur-md rounded-2xl border border-green-400/30 shadow-neon-green overflow-hidden z-20">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-400 to-transparent"></div>
                      <div className="p-4">
                        <div className="text-center mb-4">
                          <span className="text-green-400 font-mono text-sm tracking-wider">ANALYTICS CORE</span>
                        </div>
                        <div className="space-y-2">
                          <div className="p-4 rounded-xl border border-green-400/20 hover:border-green-400/40 hover:bg-green-400/5 transition-all duration-300 cursor-pointer group">
                            <div className="font-bold text-green-400 mb-1 font-mono group-hover:scale-105 transition-transform duration-300">LEARNING MATRIX</div>
                            <div className="text-xs text-gray-400 font-mono">{'>'} Study progress tracking</div>
                          </div>
                          <div className="p-4 rounded-xl border border-cyan-400/20 hover:border-cyan-400/40 hover:bg-cyan-400/5 transition-all duration-300 cursor-pointer group">
                            <div className="font-bold text-cyan-400 mb-1 font-mono group-hover:scale-105 transition-transform duration-300">VOCAB STATS</div>
                            <div className="text-xs text-gray-400 font-mono">{'>'} Word statistics</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Announcements */}
              <div className="relative">
                <button
                  onClick={() => {
                    setIsAnnounceOpen(!isAnnounceOpen);
                    setIsLanguageOpen(false);
                    setIsResourcesOpen(false);
                    setIsAnalysisOpen(false);
                    setIsMenuOpen(false);
                  }}
                  className="p-3 rounded-xl text-gray-400 hover:text-pink-400 hover:bg-pink-400/10 border border-transparent hover:border-pink-400/30 transition-all duration-300 relative group"
                >
                  <AnnounceIcon className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                  <div className="absolute -top-[-3px] -right-[-3px] w-3 h-3 bg-pink-500 rounded-full animate-pulse shadow-neon-pink"></div>
                </button>
                
                {isAnnounceOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsAnnounceOpen(false)}
                    />
                    <div className="absolute top-full right-0 mt-3 w-96 bg-black/90 backdrop-blur-md rounded-2xl border border-pink-400/30 shadow-neon-pink overflow-hidden z-20">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-pink-400 to-transparent"></div>
                      <div className="p-4 border-b border-gray-700">
                        <h3 className="font-bold text-pink-400 font-mono tracking-wider">SYSTEM ANNOUNCEMENTS</h3>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                      <div className="p-4 border-b border-gray-800 hover:bg-pink-400/5 transition-colors cursor-pointer">
                          <div className="text-sm text-gray-300 mb-2 font-mono">
                            {'>'} Nâng cấp toàn bộ giao diện UX/UI mới tối ưu hơn
                          </div>
                          <div className="text-xs text-cyan-400 font-mono">[30/06/2025]</div>
                        </div>
                        <div className="p-4 border-b border-gray-800 hover:bg-pink-400/5 transition-colors cursor-pointer">
                          <div className="text-sm text-gray-300 mb-2 font-mono">
                            {'>'} Nâng cấp tính năng resource có thể sử dụng trên điện thoại
                          </div>
                          <div className="text-xs text-cyan-400 font-mono">[11/04/2025]</div>
                        </div>
                        <div className="p-4 hover:bg-pink-400/5 transition-colors cursor-pointer">
                          <div className="text-sm text-gray-300 mb-2 font-mono">
                            {'>'} Thêm tính năng bảo mật password cho các section
                          </div>
                          <div className="text-xs text-cyan-400 font-mono">[10/04/2025]</div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Profile */}
              <div className="relative">
                {isAuthenticated ? (
                  <button
                    onClick={() => {
                      setIsMenuOpen(!isMenuOpen);
                      setIsLanguageOpen(false);
                      setIsResourcesOpen(false);
                      setIsAnalysisOpen(false);
                      setIsAnnounceOpen(false);
                    }}
                    className="p-2 rounded-xl text-gray-400 hover:text-purple-400 hover:bg-purple-400/10 border border-transparent hover:border-purple-400/30 transition-all duration-300 group"
                  >
                    <ChibaLogoNoColor className="w-8 h-8 group-hover:scale-110 transition-transform duration-300" />
                  </button>
                ) : (
                  <button
                    onClick={handleLoginClick}
                    className="px-6 py-2 rounded-xl text-cyan-400 hover:text-cyan-300 bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/30 hover:border-cyan-400/50 transition-all duration-300 font-mono font-bold shadow-neon-cyan hover:shadow-neon-cyan-intense"
                  >
                    LOGIN
                  </button>
                )}
                
                {isMenuOpen && isAuthenticated && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsMenuOpen(false)}
                    />
                    <div className="absolute top-full right-0 mt-3 w-80 bg-black/90 backdrop-blur-md rounded-2xl border border-purple-400/30 shadow-neon-purple overflow-hidden z-20">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-400 to-transparent"></div>
                      <div className="p-4">
                        <div className="text-center mb-4">
                          <span className="text-purple-400 font-mono text-sm tracking-wider">USER PROFILE</span>
                          {user && (
                            <div className="mt-2 space-y-2">
                              <div className="p-2 bg-purple-400/10 border border-purple-400/30 rounded-lg">
                                <div className="text-xs text-purple-400 font-mono">{'>'} {user.username}</div>
                                <div className="text-xs text-gray-400 font-mono">Role: {user.role}</div>
                              </div>
                              {timeLeft && (
                                <div className="p-2 bg-cyan-400/10 border border-cyan-400/30 rounded-lg">
                                  <div className="text-xs text-cyan-400 font-mono">
                                    SESSION EXPIRES:
                                  </div>
                                  <div className="text-xs text-cyan-300 font-mono">
                                    {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m
                                  </div>
                                  {timeLeft.total < 24 * 60 * 60 * 1000 && ( // Less than 1 day left
                                    <div className="text-xs text-yellow-400 font-mono mt-1">
                                      {'>'} Session expiring soon!
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          {/* <div className="p-4 rounded-xl border border-purple-400/20 hover:border-purple-400/40 hover:bg-purple-400/5 transition-all duration-300 cursor-pointer group">
                            <div className="font-bold text-purple-400 font-mono group-hover:scale-105 transition-transform duration-300">{'>'} Personal Information</div>
                          </div> */}
                          {/* <div className="p-4 rounded-xl border border-cyan-400/20 hover:border-cyan-400/40 hover:bg-cyan-400/5 transition-all duration-300 cursor-pointer group">
                            <div className="font-bold text-cyan-400 font-mono group-hover:scale-105 transition-transform duration-300">{'>'} Take Note</div>
                          </div> */}
                          <div className="p-4 rounded-xl border border-green-400/20 hover:border-green-400/40 hover:bg-green-400/5 transition-all duration-300 cursor-pointer group">
                            <div className="font-bold text-green-400 font-mono group-hover:scale-105 transition-transform duration-300">{'>'} Update Password</div>
                          </div>
                          {/* <button
                            onClick={handleExtendSession}
                            className="w-full p-4 rounded-xl border border-yellow-400/20 hover:border-yellow-400/40 hover:bg-yellow-400/5 transition-all duration-300 cursor-pointer group text-left"
                          >
                            <div className="font-bold text-yellow-400 font-mono group-hover:scale-105 transition-transform duration-300">{'>'} [EXTEND_SESSION]</div>
                            <div className="text-xs text-yellow-300 font-mono mt-1">{'>'} Add 7 more days</div>
                          </button> */}
                          <div className="border-t border-gray-700 mt-3 pt-3">
                            <button
                              onClick={handleLogout}
                              className="w-full p-4 rounded-xl border border-red-400/20 hover:border-red-400/40 hover:bg-red-400/5 transition-all duration-300 cursor-pointer group text-left"
                            >
                              <div className="font-bold text-red-400 font-mono group-hover:scale-105 transition-transform duration-300">{'>'} Disconnect</div>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-3 rounded-xl transition-all duration-300 border ${
                  isMobileMenuOpen 
                    ? "bg-cyan-400/20 text-cyan-400 border-cyan-400/50 shadow-neon-cyan"
                    : "text-gray-400 hover:text-cyan-400 hover:bg-cyan-400/10 border-transparent hover:border-cyan-400/30"
                }`}
              >
                <div className="w-6 h-6 flex flex-col justify-center items-center">
                  <span className={`bg-current block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${
                    isMobileMenuOpen ? 'rotate-45 translate-y-1' : '-translate-y-0.5'
                  }`}></span>
                  <span className={`bg-current block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm my-0.5 ${
                    isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                  }`}></span>
                  <span className={`bg-current block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${
                    isMobileMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-0.5'
                  }`}></span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-30 lg:hidden backdrop-blur-sm"
            onClick={closeAllMobileMenus}
          />
          <div className="fixed top-[70px] left-0 right-0 bg-black/95 backdrop-blur-md z-40 lg:hidden max-h-[calc(100vh-70px)] overflow-y-auto border-r border-l border-cyan-400/30">
            <div className="p-6 space-y-6 h-[100vh]">
              
              {/* Main Navigation */}
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <span className="text-cyan-400 font-mono text-sm tracking-wider">[NAVIGATION_MATRIX]</span>
                </div>
                
                <div className="space-y-3">
                  <NavLink
                    to="/"
                    className={({ isActive }) =>
                      `flex items-center gap-4 p-4 rounded-2xl transition-all duration-300 border font-mono ${
                        isActive
                          ? "bg-cyan-400/20 text-cyan-400 border-cyan-400/50 shadow-neon-cyan"
                          : "bg-black/40 text-gray-300 border-gray-700 hover:bg-cyan-400/10 hover:border-cyan-400/30 hover:text-cyan-400"
                      }`
                    }
                    onClick={closeAllMobileMenus}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${
                      ({ isActive }) => isActive 
                        ? "bg-cyan-400/20 border-cyan-400/50" 
                        : "bg-gray-800 border-gray-600"
                    }`}>
                      <span className="text-xl">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-cyan-400">
  <path d="M11.47 3.841a.75.75 0 0 1 1.06 0l8.69 8.69a.75.75 0 1 0 1.06-1.061l-8.689-8.69a2.25 2.25 0 0 0-3.182 0l-8.69 8.69a.75.75 0 1 0 1.061 1.06l8.69-8.689Z" />
  <path d="m12 5.432 8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 0 1-.75-.75v-4.5a.75.75 0 0 0-.75-.75h-3a.75.75 0 0 0-.75.75V21a.75.75 0 0 1-.75.75H5.625a1.875 1.875 0 0 1-1.875-1.875v-6.198a2.29 2.29 0 0 0 .091-.086L12 5.432Z" />
</svg>

                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="font-bold tracking-wider">HOMEPAGE</div>
                      <div className={`text-sm font-mono ${({ isActive }) => isActive ? "text-cyan-400/80" : "text-gray-500"}`}>
                        {'>'} Main terminal
                      </div>
                    </div>
                  </NavLink>
                  
{isAuthenticated && (
                    <NavLink
                      to="/resources"
                      className={({ isActive }) =>
                        `flex items-center gap-4 p-4 rounded-2xl transition-all duration-300 border font-mono ${
                          isActive
                            ? "bg-green-400/20 text-green-400 border-green-400/50 shadow-neon-green"
                            : "bg-black/40 text-gray-300 border-gray-700 hover:bg-green-400/10 hover:border-green-400/30 hover:text-green-400"
                        }`
                      }
                      onClick={closeAllMobileMenus}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${
                        ({ isActive }) => isActive 
                          ? "bg-green-400/20 border-green-400/50" 
                          : "bg-gray-800 border-gray-600"
                      }`}>
                        <span className="text-xl">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-green-400">
    <path d="M11.7 2.805a.75.75 0 0 1 .6 0A60.65 60.65 0 0 1 22.83 8.72a.75.75 0 0 1-.231 1.337 49.948 49.948 0 0 0-9.902 3.912l-.003.002c-.114.06-.227.119-.34.18a.75.75 0 0 1-.707 0A50.88 50.88 0 0 0 7.5 12.173v-.224c0-.131.067-.248.172-.311a54.615 54.615 0 0 1 4.653-2.52.75.75 0 0 0-.65-1.352 56.123 56.123 0 0 0-4.78 2.589 1.858 1.858 0 0 0-.859 1.228 49.803 49.803 0 0 0-4.634-1.527.75.75 0 0 1-.231-1.337A60.653 60.653 0 0 1 11.7 2.805Z" />
    <path d="M13.06 15.473a48.45 48.45 0 0 1 7.666-3.282c.134 1.414.22 2.843.255 4.284a.75.75 0 0 1-.46.711 47.87 47.87 0 0 0-8.105 4.342.75.75 0 0 1-.832 0 47.87 47.87 0 0 0-8.104-4.342.75.75 0 0 1-.461-.71c.035-1.442.121-2.87.255-4.286.921.304 1.83.634 2.726.99v1.27a1.5 1.5 0 0 0-.14 2.508c-.09.38-.222.753-.397 1.11.452.213.901.434 1.346.66a6.727 6.727 0 0 0 .551-1.607 1.5 1.5 0 0 0 .14-2.67v-.645a48.549 48.549 0 0 1 3.44 1.667 2.25 2.25 0 0 0 2.12 0Z" />
    <path d="M4.462 19.462c.42-.419.753-.89 1-1.395.453.214.902.435 1.347.662a6.742 6.742 0 0 1-1.286 1.794.75.75 0 0 1-1.06-1.06Z" />
</svg>

                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="font-bold tracking-wider">AI_COURSES</div>
                        <div className={`text-sm font-mono ${({ isActive }) => isActive ? "text-green-400/80" : "text-gray-500"}`}>
                          {'>'} Learning resources
                        </div>
                      </div>
                    </NavLink>
                  )}
                  
                  {isAuthenticated && (
                    <NavLink
                      to="/blogs"
                      className={({ isActive }) =>
                        `flex items-center gap-4 p-4 rounded-2xl transition-all duration-300 border font-mono ${
                          isActive
                            ? "bg-purple-400/20 text-purple-400 border-purple-400/50 shadow-neon-purple"
                            : "bg-black/40 text-gray-300 border-gray-700 hover:bg-purple-400/10 hover:border-purple-400/30 hover:text-purple-400"
                        }`
                      }
                      onClick={closeAllMobileMenus}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${
                        ({ isActive }) => isActive 
                          ? "bg-purple-400/20 border-purple-400/50" 
                          : "bg-gray-800 border-gray-600"
                      }`}>
                        <span className="text-xl">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-5 text-purple-400">
    <path fill-rule="evenodd" d="M4.125 3C3.089 3 2.25 3.84 2.25 4.875V18a3 3 0 0 0 3 3h15a3 3 0 0 1-3-3V4.875C17.25 3.839 16.41 3 15.375 3H4.125ZM12 9.75a.75.75 0 0 0 0 1.5h1.5a.75.75 0 0 0 0-1.5H12Zm-.75-2.25a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5H12a.75.75 0 0 1-.75-.75ZM6 12.75a.75.75 0 0 0 0 1.5h7.5a.75.75 0 0 0 0-1.5H6Zm-.75 3.75a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5H6a.75.75 0 0 1-.75-.75ZM6 6.75a.75.75 0 0 0-.75.75v3c0 .414.336.75.75.75h3a.75.75 0 0 0 .75-.75v-3A.75.75 0 0 0 9 6.75H6Z" clip-rule="evenodd" />
    <path d="M18.75 6.75h1.875c.621 0 1.125.504 1.125 1.125V18a1.5 1.5 0 0 1-3 0V6.75Z" />
</svg>

                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="font-bold tracking-wider">BLOG_SYSTEM</div>
                        <div className={`text-sm font-mono ${({ isActive }) => isActive ? "text-purple-400/80" : "text-gray-500"}`}>
                          {'>'} Articles & insights
                        </div>
                      </div>
                    </NavLink>
                  )}
                </div>
              </div>

              {/* Languages Section */}
              {isAuthenticated && (
                <div className="border-t border-gray-700 pt-6">
                <button
                  onClick={() => setIsMobileLanguageOpen(!isMobileLanguageOpen)}
                  className="flex items-center justify-between w-full p-4 bg-black/40 border border-gray-700 rounded-2xl hover:bg-pink-400/10 hover:border-pink-400/30 transition-all duration-300 font-mono"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-pink-400/20 border border-pink-400/50 rounded-xl flex items-center justify-center">
                      <span className="text-xl">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-5 text-pink-400">
  <path d="M15.75 8.25a.75.75 0 0 1 .75.75c0 1.12-.492 2.126-1.27 2.812a.75.75 0 1 1-.992-1.124A2.243 2.243 0 0 0 15 9a.75.75 0 0 1 .75-.75Z" />
  <path fill-rule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM4.575 15.6a8.25 8.25 0 0 0 9.348 4.425 1.966 1.966 0 0 0-1.84-1.275.983.983 0 0 1-.97-.822l-.073-.437c-.094-.565.25-1.11.8-1.267l.99-.282c.427-.123.783-.418.982-.816l.036-.073a1.453 1.453 0 0 1 2.328-.377L16.5 15h.628a2.25 2.25 0 0 1 1.983 1.186 8.25 8.25 0 0 0-6.345-12.4c.044.262.18.503.389.676l1.068.89c.442.369.535 1.01.216 1.49l-.51.766a2.25 2.25 0 0 1-1.161.886l-.143.048a1.107 1.107 0 0 0-.57 1.664c.369.555.169 1.307-.427 1.605L9 13.125l.423 1.059a.956.956 0 0 1-1.652.928l-.679-.906a1.125 1.125 0 0 0-1.906.172L4.575 15.6Z" clip-rule="evenodd" />
</svg>

                      </span>
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-pink-400 tracking-wider">LANGUAGES</div>
                      <div className="text-sm text-gray-500 font-mono">{'>'} English & Japanese</div>
                    </div>
                  </div>
                  <IconChevronDown className={`w-5 h-5 text-pink-400 transition-transform duration-300 ${isMobileLanguageOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isMobileLanguageOpen && (
                  <div className="mt-4 space-y-4">
                    {/* English */}
                    <div className="bg-cyan-400/10 border border-cyan-400/30 rounded-2xl p-4">
                      <div className="flex items-center gap-3 mb-4">
                        <EnglishFlag className="w-6 h-6" />
                        <div className="font-bold text-cyan-400 font-mono tracking-wider">ENGLISH</div>
                      </div>
                      <div className="space-y-2">
                        <NavLink 
                          to="/repeat-audio"
                          className="flex items-center gap-3 p-3 bg-black/40 border border-cyan-400/20 rounded-xl hover:bg-cyan-400/10 hover:border-cyan-400/40 transition-all duration-300 font-mono"
                          onClick={closeAllMobileMenus}
                        >
                          <Volumeware className="w-5 h-5 fill-cyan-400" />
                          <span className="font-medium text-cyan-400">{'>'} Repeat Audio</span>
                        </NavLink>
                        <NavLink 
                          to="/vocabulary-daily"
                          className="flex items-center gap-3 p-3 bg-black/40 border border-cyan-400/20 rounded-xl hover:bg-cyan-400/10 hover:border-cyan-400/40 transition-all duration-300 font-mono"
                          onClick={closeAllMobileMenus}
                        >
                          <VocabDaily className="w-5 h-5 fill-cyan-400" />
                          <span className="font-medium text-cyan-400">{'>'} Vocabulary Daily</span>
                        </NavLink>
                      </div>
                    </div>
                    
                    {/* Japanese */}
                    <div className="bg-pink-400/10 border border-pink-400/30 rounded-2xl p-4">
                      <div className="flex items-center gap-3 mb-4">
                        <JapaneseFlag className="w-6 h-6" />
                        <div className="font-bold text-pink-400 font-mono tracking-wider">JAPANESE</div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-3 p-3 bg-black/40 border border-pink-400/20 rounded-xl hover:bg-pink-400/10 hover:border-pink-400/40 transition-all duration-300 cursor-pointer font-mono">
                          <IconVoice className="w-5 h-5 fill-pink-400" />
                          <span className="font-medium text-pink-400">{'>'} Kaiwa (AI)</span>
                        </div>
                        <NavLink 
                          to="/vocabulary-japanese"
                          className="flex items-center gap-3 p-3 bg-black/40 border border-pink-400/20 rounded-xl hover:bg-pink-400/10 hover:border-pink-400/40 transition-all duration-300 font-mono"
                          onClick={closeAllMobileMenus}
                        >
                          <VocabDaily className="w-5 h-5 fill-pink-400" />
                          <span className="font-medium text-pink-400">{'>'} Japanese Vocabulary</span>
                        </NavLink>
                      </div>
                    </div>
                  </div>
                )}
                </div>
              )}

              {/* Authentication Section */}
              <div className="border-t border-gray-700 pt-6">
                {isAuthenticated ? (
                  <div className="space-y-4">
                    <div className="text-center mb-4">
                      {/* <span className="text-purple-400 font-mono text-sm tracking-wider">USER PROFILE</span> */}
                      {user && (
                        <div className="mt-2 space-y-2">
                          <div className="p-2 bg-purple-400/10 border border-purple-400/30 rounded-lg">
                            <div className="text-xs text-purple-400 font-mono">{'>'} {user.username}</div>
                            <div className="text-xs text-gray-400 font-mono">Role: {user.role}</div>
                          </div>
                          {timeLeft && (
                            <div className="p-2 bg-cyan-400/10 border border-cyan-400/30 rounded-lg">
                              <div className="text-xs text-cyan-400 font-mono">
                                SESSION EXPIRES:
                              </div>
                              <div className="text-xs text-cyan-300 font-mono">
                                {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m
                              </div>
                              {timeLeft.total < 24 * 60 * 60 * 1000 && (
                                <div className="text-xs text-yellow-400 font-mono mt-1">
                                  {'>'} Session expiring soon!
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      {/* <button
                        onClick={handleExtendSession}
                        className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border border-yellow-400/20 hover:border-yellow-400/40 hover:bg-yellow-400/5 transition-all duration-300 font-mono font-bold text-yellow-400"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>EXTEND SESSION</span>
                      </button> */}
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border border-red-400/20 hover:border-red-400/40 hover:bg-red-400/5 transition-all duration-300 font-mono font-bold text-red-400"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span>DISCONNECT</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="mb-4">
                      <span className="text-cyan-400 font-mono text-sm tracking-wider">ACCESS REQUIRED</span>
                    </div>
                    <button
                      onClick={handleLoginClick}
                      className="w-full flex items-center justify-center gap-3 p-4 rounded-2xl bg-cyan-400/10 border border-cyan-400/30 hover:bg-cyan-400/20 hover:border-cyan-400/50 transition-all duration-300 font-mono font-bold text-cyan-400 shadow-neon-cyan hover:shadow-neon-cyan-intense transform hover:scale-105"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                      <span>LOGIN ACCESS</span>
                    </button>
                    <div className="mt-3 text-xs text-gray-500 font-mono">
                      {'>'} Đăng nhập để truy cập tính năng đầy đủ
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              {isAuthenticated && (
                <div className="border-t border-gray-700 pt-6">
                <div className="text-sm font-bold text-gray-400 mb-4 font-mono tracking-wider">QUICK ACCESS</div>
                
                <div className="grid grid-cols-4 gap-3">
                  <div className="flex flex-col items-center p-4 bg-black/40 border border-gray-700 rounded-xl hover:bg-green-400/10 hover:border-green-400/30 transition-all duration-300 cursor-pointer group">
                    <div className="w-10 h-10 bg-green-400/20 border border-green-400/50 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-300">
                      <span className="text-sm">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-4 text-green-400">
  <path d="M11.25 4.533A9.707 9.707 0 0 0 6 3a9.735 9.735 0 0 0-3.25.555.75.75 0 0 0-.5.707v14.25a.75.75 0 0 0 1 .707A8.237 8.237 0 0 1 6 18.75c1.995 0 3.823.707 5.25 1.886V4.533ZM12.75 20.636A8.214 8.214 0 0 1 18 18.75c.966 0 1.89.166 2.75.47a.75.75 0 0 0 1-.708V4.262a.75.75 0 0 0-.5-.707A9.735 9.735 0 0 0 18 3a9.707 9.707 0 0 0-5.25 1.533v16.103Z" />
</svg>

                      </span>
                    </div>
                    <div className="text-xs font-bold text-green-400 text-center font-mono">RES</div>
                  </div>
                  
                  <div className="flex flex-col items-center p-4 bg-black/40 border border-gray-700 rounded-xl hover:bg-purple-400/10 hover:border-purple-400/30 transition-all duration-300 cursor-pointer group">
                    <div className="w-10 h-10 bg-purple-400/20 border border-purple-400/50 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-300">
                      <span className="text-sm">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-4 text-purple-400">
  <path fill-rule="evenodd" d="M2.25 2.25a.75.75 0 0 0 0 1.5H3v10.5a3 3 0 0 0 3 3h1.21l-1.172 3.513a.75.75 0 0 0 1.424.474l.329-.987h8.418l.33.987a.75.75 0 0 0 1.422-.474l-1.17-3.513H18a3 3 0 0 0 3-3V3.75h.75a.75.75 0 0 0 0-1.5H2.25Zm6.54 15h6.42l.5 1.5H8.29l.5-1.5Zm8.085-8.995a.75.75 0 1 0-.75-1.299 12.81 12.81 0 0 0-3.558 3.05L11.03 8.47a.75.75 0 0 0-1.06 0l-3 3a.75.75 0 1 0 1.06 1.06l2.47-2.47 1.617 1.618a.75.75 0 0 0 1.146-.102 11.312 11.312 0 0 1 3.612-3.321Z" clip-rule="evenodd" />
</svg>

                      </span>
                    </div>
                    <div className="text-xs font-bold text-purple-400 text-center font-mono">STAT</div>
                  </div>
                  
                  <div className="flex flex-col items-center p-4 bg-black/40 border border-gray-700 rounded-xl hover:bg-yellow-400/10 hover:border-yellow-400/30 transition-all duration-300 cursor-pointer group">
                    <div className="w-10 h-10 bg-yellow-400/20 border border-yellow-400/50 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-300">
                      <span className="text-sm">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-4 text-yellow-400">
  <path d="M16.881 4.345A23.112 23.112 0 0 1 8.25 6H7.5a5.25 5.25 0 0 0-.88 10.427 21.593 21.593 0 0 0 1.378 3.94c.464 1.004 1.674 1.32 2.582.796l.657-.379c.88-.508 1.165-1.593.772-2.468a17.116 17.116 0 0 1-.628-1.607c1.918.258 3.76.75 5.5 1.446A21.727 21.727 0 0 0 18 11.25c0-2.414-.393-4.735-1.119-6.905ZM18.26 3.74a23.22 23.22 0 0 1 1.24 7.51 23.22 23.22 0 0 1-1.41 7.992.75.75 0 1 0 1.409.516 24.555 24.555 0 0 0 1.415-6.43 2.992 2.992 0 0 0 .836-2.078c0-.807-.319-1.54-.836-2.078a24.65 24.65 0 0 0-1.415-6.43.75.75 0 1 0-1.409.516c.059.16.116.321.17.483Z" />
</svg>

                      </span>
                    </div>
                    <div className="text-xs font-bold text-yellow-400 text-center font-mono">NEWS</div>
                  </div>
                  
                  <div className="flex flex-col items-center p-4 bg-black/40 border border-gray-700 rounded-xl hover:bg-gray-400/10 hover:border-gray-400/30 transition-all duration-300 cursor-pointer group">
                    <div className="w-10 h-10 bg-gray-400/20 border border-gray-400/50 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-300">
                      <span className="text-sm">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-5 text-gray-400">
  <path fill-rule="evenodd" d="M18.685 19.097A9.723 9.723 0 0 0 21.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 0 0 3.065 7.097A9.716 9.716 0 0 0 12 21.75a9.716 9.716 0 0 0 6.685-2.653Zm-12.54-1.285A7.486 7.486 0 0 1 12 15a7.486 7.486 0 0 1 5.855 2.812A8.224 8.224 0 0 1 12 20.25a8.224 8.224 0 0 1-5.855-2.438ZM15.75 9a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" clip-rule="evenodd" />
</svg>

                      </span>
                    </div>
                    <div className="text-xs font-bold text-gray-400 text-center font-mono">USER</div>
                  </div>
                </div>
                </div>
              )}
              
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        @keyframes slide-right {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }

        .animate-slide-right { animation: slide-right 3s ease-in-out infinite; }

        .shadow-neon-cyan { box-shadow: 0 0 20px rgba(0,255,255,0.5), 0 0 40px rgba(0,255,255,0.3), 0 0 60px rgba(0,255,255,0.1); }
        .shadow-neon-pink { box-shadow: 0 0 20px rgba(255,0,128,0.5), 0 0 40px rgba(255,0,128,0.3), 0 0 60px rgba(255,0,128,0.1); }
        .shadow-neon-green { box-shadow: 0 0 20px rgba(0,255,128,0.5), 0 0 40px rgba(0,255,128,0.3), 0 0 60px rgba(0,255,128,0.1); }
        .shadow-neon-purple { box-shadow: 0 0 20px rgba(128,0,255,0.5), 0 0 40px rgba(128,0,255,0.3), 0 0 60px rgba(128,0,255,0.1); }
      `}</style>
    </>
  );
}
